const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

export default reorder;

const byId = (task, id) => Number(task.taskId) === Number(id);

const insertAmongVisible = (fullWithout, visibleWithMoved, draggedId) => {
    const movedIndex = visibleWithMoved.findIndex((task) => byId(task, draggedId));
    const next = visibleWithMoved[movedIndex + 1];
    if (next) {
        const idx = fullWithout.findIndex((task) => byId(task, next.taskId));
        return idx === -1 ? fullWithout.length : idx;
    }

    const prev = visibleWithMoved[movedIndex - 1];
    if (prev) {
        const idx = fullWithout.findIndex((task) => byId(task, prev.taskId));
        return idx === -1 ? fullWithout.length : idx + 1;
    }

    return 0;
};

export const reorderBetweenColumns = ({
    columns,
    source,
    destination,
    taskId,
    getVisibleTasks,
}) => {
    const sourceStatus = Number(source.droppableId);
    const destStatus = Number(destination.droppableId);
    const draggedId = Number(taskId);

    const sourceColumn = columns.find((column) => Number(column.status) === sourceStatus);
    const destColumn = columns.find((column) => Number(column.status) === destStatus);
    if (!sourceColumn || !destColumn) {
        return { columns };
    }

    const targetTask = sourceColumn.tasks.find((task) => byId(task, draggedId));
    if (!targetTask) {
        return { columns };
    }

    const visibleOf = typeof getVisibleTasks === 'function'
        ? getVisibleTasks
        : (tasks) => tasks;

    const movedTask = { ...targetTask, status: destStatus };
    const sameColumn = sourceStatus === destStatus;

    if (sameColumn) {
        const visible = visibleOf(sourceColumn.tasks);
        const reorderedVisible = reorder(visible, source.index, destination.index);
        const without = sourceColumn.tasks.filter((task) => !byId(task, draggedId));
        const insertAt = insertAmongVisible(without, reorderedVisible, draggedId);
        const nextTasks = [...without];
        nextTasks.splice(insertAt, 0, movedTask);

        return {
            columns: columns.map((column) => (
                Number(column.status) === sourceStatus
                    ? { ...column, tasks: nextTasks }
                    : column
            )),
        };
    }

    const destVisible = visibleOf(destColumn.tasks);
    const newDestVisible = [...destVisible];
    newDestVisible.splice(destination.index, 0, movedTask);
    const destWithout = destColumn.tasks.filter((task) => !byId(task, draggedId));
    const insertAt = insertAmongVisible(destWithout, newDestVisible, draggedId);
    const nextDestTasks = [...destWithout];
    nextDestTasks.splice(insertAt, 0, movedTask);
    const nextSourceTasks = sourceColumn.tasks.filter((task) => !byId(task, draggedId));

    return {
        columns: columns.map((column) => {
            if (Number(column.status) === sourceStatus) {
                return { ...column, tasks: nextSourceTasks };
            }
            if (Number(column.status) === destStatus) {
                return { ...column, tasks: nextDestTasks };
            }
            return column;
        }),
    };
};

export const reorderMultipleBetweenColumns = ({
    columns,
    columnOrder,
    source,
    destination,
    draggedId,
    selectedIds,
    getVisibleTasks,
}) => {
    const idSet = new Set((selectedIds || []).map(Number));
    const dragged = Number(draggedId);

    if (!idSet.has(dragged) || idSet.size <= 1) {
        return reorderBetweenColumns({
            columns,
            source,
            destination,
            taskId: draggedId,
            getVisibleTasks,
        });
    }

    const destStatus = Number(destination.droppableId);
    const sourceStatus = Number(source.droppableId);
    const visibleOf = typeof getVisibleTasks === 'function'
        ? getVisibleTasks
        : (tasks) => tasks;

    const orderedStatuses = Array.isArray(columnOrder) && columnOrder.length > 0
        ? columnOrder
        : columns.map((column) => column.status);

    const moving = [];
    orderedStatuses.forEach((status) => {
        const column = columns.find((item) => Number(item.status) === Number(status));
        if (!column) {
            return;
        }
        column.tasks.forEach((task) => {
            if (idSet.has(Number(task.taskId))) {
                moving.push(task);
            }
        });
    });

    if (moving.length === 0) {
        return { columns };
    }

    const destColumn = columns.find((column) => Number(column.status) === destStatus);
    if (!destColumn) {
        return { columns };
    }

    const destVisible = visibleOf(destColumn.tasks);
    let insertAtVisible = destination.index;

    if (sourceStatus === destStatus) {
        const withoutDragged = destVisible.filter((task) => Number(task.taskId) !== dragged);
        const selectedBefore = withoutDragged
            .slice(0, destination.index)
            .filter((task) => idSet.has(Number(task.taskId)))
            .length;
        insertAtVisible = destination.index - selectedBefore;
    } else {
        const selectedBefore = destVisible
            .slice(0, destination.index)
            .filter((task) => idSet.has(Number(task.taskId)))
            .length;
        insertAtVisible = destination.index - selectedBefore;
    }

    insertAtVisible = Math.max(0, insertAtVisible);

    const movingIds = new Set(moving.map((task) => Number(task.taskId)));
    const movedTasks = moving.map((task) => ({ ...task, status: destStatus }));
    const destWithout = destColumn.tasks.filter((task) => !movingIds.has(Number(task.taskId)));
    const destVisibleWithout = destVisible.filter((task) => !movingIds.has(Number(task.taskId)));
    const clamped = Math.min(insertAtVisible, destVisibleWithout.length);
    const newDestVisible = [...destVisibleWithout];
    newDestVisible.splice(clamped, 0, ...movedTasks);

    const afterBlock = newDestVisible[clamped + movedTasks.length];
    const beforeBlock = newDestVisible[clamped - 1];
    let insertAtFull = destWithout.length;
    if (afterBlock) {
        const idx = destWithout.findIndex((task) => byId(task, afterBlock.taskId));
        insertAtFull = idx === -1 ? destWithout.length : idx;
    } else if (beforeBlock) {
        const idx = destWithout.findIndex((task) => byId(task, beforeBlock.taskId));
        insertAtFull = idx === -1 ? destWithout.length : idx + 1;
    } else {
        insertAtFull = 0;
    }

    const nextDestTasks = [...destWithout];
    nextDestTasks.splice(insertAtFull, 0, ...movedTasks);

    return {
        columns: columns.map((column) => {
            if (Number(column.status) === destStatus) {
                return { ...column, tasks: nextDestTasks };
            }
            return {
                ...column,
                tasks: column.tasks.filter((task) => !movingIds.has(Number(task.taskId))),
            };
        }),
    };
};

export function moveBetween({ list1, list2, source, destination }) {
    const newFirst = Array.from(list1.values);
    const newSecond = Array.from(list2.values);

    const moveFrom = source.droppableId === list1.id ? newFirst : newSecond;
    const moveTo = moveFrom === newFirst ? newSecond : newFirst;

    const [moved] = moveFrom.splice(source.index, 1);
    moveTo.splice(destination.index, 0, moved);

    return {
        list1: {
            ...list1,
            values: newFirst
        },
        list2: {
            ...list2,
            values: newSecond
        }
    };
};
